import { IUser } from "models/user/user.model";
import {
  ExceptionNotificationSettings,
  ExceptionNotificationSubscription,
  ExceptionNotificationsResponse,
  NotificationCadence,
} from "services/api/exceptionNotifications";
import {
  isUserAdminForSalesAccounts,
  isUserSalesPeopleForSalesAccounts,
} from "utils/isUserAdminForSalesAccounts";

// Exceptions v2 is for ITD staff only (sales-account admins and
// salespeople): unlike v1 there is no per-account "Show Exceptions page"
// opt-in. The server's ItdSalesAdminOrSalespeopleOnlyGuard enforces the same
// rule on the notification settings API.
export const canSeeExceptionsV2 = (user: IUser): boolean =>
  isUserAdminForSalesAccounts(user) || isUserSalesPeopleForSalesAccounts(user);

export const CADENCE_OPTIONS: {
  value: NotificationCadence;
  label: string;
  hint: string;
}[] = [
  {
    value: "immediate",
    label: "Immediately",
    hint: "Checked every 15 minutes, one email per batch of new exceptions",
  },
  { value: "daily", label: "Daily digest", hint: "One email at 08:00 UK time" },
  {
    value: "weekly",
    label: "Weekly digest",
    hint: "One email on Mondays at 08:00 UK time",
  },
];

const CADENCE_SHORT: Record<NotificationCadence, string> = {
  immediate: "immediate",
  daily: "daily",
  weekly: "weekly",
};

const CADENCE_SENTENCE: Record<NotificationCadence, string> = {
  immediate: "within 15 minutes of each new exception",
  daily: "daily at 08:00 UK time",
  weekly: "on Mondays at 08:00 UK time",
};

export const fmtDayTime = (iso: string) =>
  new Date(iso).toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });

/** "DSV and DPD", "DSV, DPD and Evri". */
const listNames = (names: string[]): string =>
  names.length <= 1
    ? names.join("")
    : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;

/** Which carriers an email covers, in words. */
export const carrierScopeText = (
  settings: Pick<
    ExceptionNotificationSettings,
    "includedCarriers" | "excludedCarriers"
  >
): string => {
  if (settings.includedCarriers.length > 0) {
    return `${listNames(settings.includedCarriers)} only`;
  }
  if (settings.excludedCarriers.length > 0) {
    return `All carriers except ${listNames(settings.excludedCarriers)}`;
  }
  return "All carriers";
};

/** Whether an email covers a carrier (names compared ignoring case). */
export const coversCarrier = (
  settings: Pick<
    ExceptionNotificationSettings,
    "includedCarriers" | "excludedCarriers"
  >,
  carrier: string
): boolean => {
  const lower = carrier.toLowerCase();
  if (settings.includedCarriers.length > 0) {
    return settings.includedCarriers.some((c) => c.toLowerCase() === lower);
  }
  return !settings.excludedCarriers.some((c) => c.toLowerCase() === lower);
};

/** What the emails list calls an email: its name, else what it covers. */
export const subscriptionTitle = (
  sub: Pick<
    ExceptionNotificationSettings,
    "name" | "includedCarriers" | "excludedCarriers"
  >
): string => sub.name.trim() || carrierScopeText(sub);

/**
 * The Notifications button summary: "Off", "6 types · daily" for one email,
 * "3 emails" for several (switched-off emails not counted).
 */
export const notificationButtonSummary = (
  data: ExceptionNotificationsResponse | null
): string | null => {
  if (!data) return null;
  const on = data.subscriptions.filter((sub) => sub.categories.length > 0);
  if (on.length === 0) return "Off";
  if (on.length > 1) return `${on.length} emails`;
  const n = on[0].categories.length;
  return `${n} ${n === 1 ? "type" : "types"} · ${CADENCE_SHORT[on[0].cadence]}`;
};

/** One-line status of one SAVED email (null: a new, unsaved email). */
export const notificationStatusLine = (
  data: ExceptionNotificationsResponse,
  sub: ExceptionNotificationSubscription | null
): { on: boolean; text: string } => {
  if (!sub) {
    return {
      on: false,
      text: "Not saved yet. Choose what to hear about, then save.",
    };
  }
  if (sub.categories.length === 0) {
    return { on: false, text: "Off: no exception types are switched on." };
  }
  const lastChecked = sub.lastRunAt
    ? ` Last checked ${fmtDayTime(sub.lastRunAt)}.`
    : "";
  return {
    on: true,
    text: `On: ${sub.categories.length} of ${
      data.categories.length
    } types, emailed ${CADENCE_SENTENCE[sub.cadence]} to ${
      data.recipient
    }.${lastChecked}`,
  };
};

/** Short "Daily digest" style label for the emails list. */
export const cadenceLabel = (cadence: NotificationCadence): string =>
  CADENCE_OPTIONS.find((option) => option.value === cadence)?.label ?? cadence;

/** Parcels at or over a day threshold, from one of the server's histograms. */
export const countAtOrOver = (histogram: number[], days: number) =>
  histogram.slice(days).reduce((sum, n) => sum + n, 0);

const sameNames = (a: string[], b: string[]) =>
  [...a].sort().join() === [...b].sort().join();

export const sameSettings = (
  a: ExceptionNotificationSettings,
  b: ExceptionNotificationSettings
) =>
  a.name.trim() === b.name.trim() &&
  a.cadence === b.cadence &&
  a.stuckDays === b.stuckDays &&
  a.neverScannedDays === b.neverScannedDays &&
  sameNames(a.categories, b.categories) &&
  sameNames(a.includedCarriers, b.includedCarriers) &&
  sameNames(a.excludedCarriers, b.excludedCarriers);
